import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Panel } from "@/components/Panel";
import { toast } from "sonner";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import {
  ArrowLeft, RefreshCw, Database, ShoppingCart, Users, Cpu, Briefcase, MessageCircle, Truck,
  Brain, DollarSign, TrendingUp, AlertTriangle, Target, ChevronRight, Network, Sparkles, Loader2,
  Upload, GitCompare, Plus, Minus, ArrowUpRight, ArrowDownRight,
  ShieldCheck, Award, XCircle, AlertOctagon, CheckCircle2, LineChart,
} from "lucide-react";
import ComparePlaybooksPanel from "@/components/ComparePlaybooksPanel";
import CashflowMappingPanel from "@/components/CashflowMappingPanel";

const ICONS = {
  POS: ShoppingCart, CRM: Users, IOT: Cpu, ERP: Briefcase,
  SOCIAL: MessageCircle, SUPPLY_CHAIN: Truck,
};
const TYPE_ICON = { TREND: TrendingUp, ANOMALY: AlertTriangle, PREDICTION: Target };
const TYPE_COLOR = {
  TREND: "text-[#FFB000] border-[#FFB000]/30",
  ANOMALY: "text-[#FF3B30] border-[#FF3B30]/30",
  PREDICTION: "text-[#00E676] border-[#00E676]/30",
};
const STATUS_COLORS = {
  ACTIVE: "text-[#00E676] border-[#00E676]/30",
  SYNCING: "text-[#FFB000] border-[#FFB000]/30",
  OFFLINE: "text-[#FF3B30] border-[#FF3B30]/30",
};

const fmtNum = (n) =>
  n >= 1e6 ? `${(n/1e6).toFixed(2)}M` :
  n >= 1e3 ? `${(n/1e3).toFixed(1)}K` :
  String(n);

// Currency symbols for the ISO codes users can pick in Analysis Context.
const CURRENCY_SYMBOLS = {
  USD: "$", EUR: "€", GBP: "£", NGN: "₦", ZAR: "R", KES: "KSh",
  GHS: "₵", EGP: "£", INR: "₹", BRL: "R$", MXN: "$", AED: "د.إ",
  SAR: "﷼", JPY: "¥", CNY: "¥", AUD: "A$", CAD: "C$", SGD: "S$",
};

const fmtCurrency = (n, code) => {
  const sym = CURRENCY_SYMBOLS[code] || (code ? `${code} ` : "$");
  if (n == null) return `${sym}0`;
  const abs = Math.abs(n);
  if (abs >= 1e9) return `${sym}${(n/1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${sym}${(n/1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${sym}${(n/1e3).toFixed(1)}K`;
  return `${sym}${Math.round(n)}`;
};

export default function DataSourceDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [reuploading, setReuploading] = useState(false);
  const [scorecard, setScorecard] = useState(null);
  const [scoring, setScoring] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [wsCcy, setWsCcy] = useState("USD");
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/data-sources/${id}`);
      setData(r.data);
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  useEffect(() => {
    api.get("/workspace").then(r => {
      const code = r.data?.analysis_context?.currency_code;
      if (code) setWsCcy(code.toUpperCase());
    }).catch(() => {});
  }, []);

  const sync = async () => {
    setSyncing(true);
    try {
      await api.post(`/data-sources/${id}/sync`);
      await load();
      toast.success("Source synced.");
    } catch { toast.error("Sync failed"); }
    setSyncing(false);
  };

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const r = await api.post(`/data-sources/${id}/analyze`);
      const n = r.data?.generated || 0;
      const diff = r.data?.diff_aware;
      toast.success(`${diff ? "Diff-aware " : ""}analysis · ${n} insight${n === 1 ? "" : "s"}.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analyze failed");
    }
    setAnalyzing(false);
  };

  const runScorecard = async () => {
    setScoring(true);
    try {
      const r = await api.post(`/data-sources/${id}/scorecard`);
      setScorecard(r.data);
      toast.success(`Scorecard · ${r.data.overall_score}/100 against ${r.data.playbook_name}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scorecard failed");
    }
    setScoring(false);
  };

  const onPickReupload = () => fileRef.current?.click();

  const onReupload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Please select a .csv file");
      e.target.value = "";
      return;
    }
    setReuploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/data-sources/${id}/reupload`, fd);
      const newRows = r.data?.records_count ?? 0;
      const diff = r.data?.profile_diff;
      if (diff) {
        const rc = diff.row_count || {};
        const pct = rc.pct != null ? ` (${rc.pct > 0 ? "+" : ""}${rc.pct}%)` : "";
        toast.success(`CSV re-uploaded · ${newRows.toLocaleString()} rows${pct}. Run ANALYZE for a diff-aware read.`);
      } else {
        toast.success(`CSV uploaded · ${newRows.toLocaleString()} rows profiled.`);
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Re-upload failed");
    }
    setReuploading(false);
    e.target.value = "";
  };

  if (loading) return <div className="p-10 font-mono text-[11px] text-zinc-500">LOADING SOURCE…</div>;
  if (notFound) return (
    <div className="p-10" data-testid="source-not-found">
      <h1 className="font-heading font-black text-3xl uppercase tracking-tighter">404 — Source not found.</h1>
      <Link to="/app/sources" className="font-mono text-[11px] uppercase tracking-widest text-[#FFB000] hover:underline mt-4 inline-block">
        ← Back to sources
      </Link>
    </div>
  );
  if (!data) return null;

  const { source: s, insights, opportunities, pairs_with, summary, contribution_series } = data;
  const Icon = ICONS[s.type] || Database;

  return (
    <div className="p-6 space-y-6" data-testid="source-detail">
      {/* breadcrumb */}
      <button
        onClick={() => nav("/app/sources")}
        data-testid="back-to-sources"
        className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-[#FFB000] flex items-center gap-1.5"
      >
        <ArrowLeft className="w-3 h-3" /> All sources
      </button>

      {/* header */}
      <header className="flex items-start justify-between gap-6 flex-wrap pb-6 border-b border-zinc-900">
        <div className="flex items-start gap-4 min-w-0 flex-1">
          <div className="w-14 h-14 border border-[#FFB000]/40 bg-[#FFB000]/10 flex items-center justify-center flex-shrink-0">
            <Icon className="w-7 h-7 text-[#FFB000] stroke-[1.5]" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-3 mb-1">
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">SOURCE · {s.type}</span>
              <span className={`font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 border ${STATUS_COLORS[s.status]}`}>{s.status}</span>
              <span className="font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 border border-zinc-800 text-zinc-400">{s.origin || "SEEDED"}</span>
            </div>
            <h1 className="font-heading font-black text-4xl tracking-tighter uppercase">{s.name}</h1>
            <p className="text-sm text-zinc-500 mt-2 max-w-2xl leading-relaxed">{s.description}</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {s.origin === "CSV" && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={onReupload}
                data-testid="detail-reupload-input"
              />
              <button
                onClick={onPickReupload}
                disabled={reuploading}
                data-testid="detail-reupload-btn"
                className="border border-zinc-700 px-4 py-2.5 font-mono text-[11px] uppercase tracking-widest hover:bg-zinc-900 hover:text-[#FFB000] hover:border-[#FFB000]/50 flex items-center gap-2 disabled:opacity-50"
                title="Re-upload the latest CSV to get a week-over-week diff and a focused DARA analysis on what changed."
              >
                {reuploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {reuploading ? "UPLOADING…" : "RE-UPLOAD CSV"}
              </button>
            </>
          )}
          {s.profile && (
            <button
              onClick={runScorecard}
              disabled={scoring}
              data-testid="detail-scorecard-btn"
              className="border border-[#00BFFF]/40 text-[#00BFFF] px-4 py-2.5 font-mono text-[11px] uppercase tracking-widest hover:bg-[#00BFFF]/10 disabled:opacity-50 flex items-center gap-2 transition-colors"
              title="Score this source against a market-specific regulator-sourced playbook (e.g. CBN / NIBSS / EFInA benchmarks for Nigerian banking)."
            >
              {scoring ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
              {scoring ? "SCORING…" : "MARKET SCORECARD"}
            </button>
          )}
          {s.profile && (
            <button
              onClick={analyze}
              disabled={analyzing}
              data-testid="detail-analyze-btn"
              style={{ backgroundColor: "var(--brand-accent, #FFB000)", color: "var(--brand-accent-text, #000)" }}
              className="font-bold font-mono text-[11px] uppercase tracking-widest px-4 py-2.5 hover:opacity-90 disabled:opacity-50 flex items-center gap-2 transition-opacity"
              title={s.profile_diff ? "Run DARA diff-aware — prioritizes what changed since the prior upload." : "Run DARA over the column profile and generate grounded insights pinned to this source."}
            >
              {analyzing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {analyzing ? "ANALYZING…" : s.profile_diff ? "ANALYZE (DIFF-AWARE)" : "ANALYZE WITH DARA"}
            </button>
          )}
          <button
            onClick={sync}
            disabled={syncing}
            data-testid="detail-sync-btn"
            className="border border-zinc-700 px-4 py-2.5 font-mono text-[11px] uppercase tracking-widest hover:bg-zinc-900 hover:text-[#FFB000] hover:border-[#FFB000]/50 flex items-center gap-2 disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "SYNCING…" : "SYNC NOW"}
          </button>
        </div>
      </header>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-zinc-900 border border-zinc-900" data-testid="source-kpis">
        <Kpi label="RECORDS" value={fmtNum(s.records_count)} accent="text-white" />
        <Kpi label="HEALTH" value={`${s.health_score}%`} accent={s.health_score >= 90 ? "text-[#00E676]" : s.health_score >= 75 ? "text-[#FFB000]" : "text-[#FF3B30]"} />
        <Kpi label="MAPPED INSIGHTS" value={summary.insights_count} accent="text-[#FFB000]" />
        <Kpi label="REVENUE PATHS" value={summary.opportunities_count} accent="text-[#FFB000]" />
        <Kpi label="ATTRIBUTED ARR" value={fmtCurrency(summary.attributed_revenue_arr, wsCcy)} accent="text-[#00E676]" />
      </div>

      {/* CHANGES SINCE LAST UPLOAD (only when prior_profile exists) */}
      {s.profile_diff && <DiffPanel diff={s.profile_diff} />}

      {/* MARKET SCORECARD (Phase 21) */}
      {scorecard && <ScorecardPanel sc={scorecard} sourceId={id} />}

      {/* CASH-FLOW MAPPING (Phase 35A) — only for CSV sources */}
      <CashflowMappingPanel source={data} />

      {/* COMPARE PLAYBOOKS (Phase 33C) — appears once user has run a base scorecard */}
      {scorecard && (
        <ComparePlaybooksPanel sourceId={id} defaultPlaybookId={scorecard.playbook_id} />
      )}

      {/* COLUMN PROFILE (only when this source was uploaded as CSV with a profile) */}
      {s.profile && (
        <Panel title={`// COLUMN PROFILE · ${s.profile.columns?.length || 0} COLUMNS · ${(s.profile.row_count || 0).toLocaleString()} ROWS`} testId="source-profile" meta={<Sparkles className="w-3.5 h-3.5 text-[#FFB000]" />}>
          <div className="overflow-x-auto">
            <table className="w-full font-mono text-[11px]">
              <thead>
                <tr className="text-zinc-500 uppercase tracking-widest text-[9px] border-b border-zinc-900">
                  <th className="text-left px-4 py-2">Column</th>
                  <th className="text-left px-4 py-2">Type</th>
                  <th className="text-right px-4 py-2">Nulls</th>
                  <th className="text-right px-4 py-2">Cardinality</th>
                  <th className="text-left px-4 py-2">Stats / Top Values</th>
                </tr>
              </thead>
              <tbody>
                {s.profile.columns.map((c, i) => (
                  <tr key={c.name + i} className="border-b border-zinc-900/50 hover:bg-zinc-900/30" data-testid={`profile-row-${i}`}>
                    <td className="px-4 py-2 text-white">{c.name}</td>
                    <td className="px-4 py-2">
                      <span className={`px-1.5 py-0.5 border text-[9px] uppercase tracking-widest ${c.type === "numeric" ? "border-[#00BFFF]/40 text-[#00BFFF]" : c.type === "date" ? "border-[#FFB000]/40 text-[#FFB000]" : "border-zinc-700 text-zinc-400"}`}>{c.type}</span>
                    </td>
                    <td className={`px-4 py-2 text-right ${c.null_pct > 20 ? "text-[#FF3B30]" : c.null_pct > 5 ? "text-[#FFB000]" : "text-zinc-400"}`}>
                      {c.null_pct}%
                    </td>
                    <td className="px-4 py-2 text-right text-zinc-300">{c.cardinality.toLocaleString()}</td>
                    <td className="px-4 py-2 text-zinc-500">
                      {c.numeric ? (
                        <span>min={c.numeric.min} · max={c.numeric.max} · μ={c.numeric.mean}</span>
                      ) : (
                        <span className="line-clamp-1">
                          {(c.top_values || []).slice(0, 3).map(t => `${t.value}×${t.count}`).join(" · ") || "—"}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {s.profile.join_candidates?.length > 0 && (
            <div className="border-t border-zinc-900 px-4 py-3 font-mono text-[10px] text-zinc-500">
              Suspected join keys: {s.profile.join_candidates.map(k => (
                <span key={k} className="ml-1 px-1.5 py-0.5 border border-zinc-700 text-zinc-300">{k}</span>
              ))}
            </div>
          )}
        </Panel>
      )}

      {/* revenue contribution chart + co-occurring sources */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Panel title="// THIS SOURCE → REVENUE · 12M" testId="contrib-chart" meta={`Total: ${fmtCurrency(summary.attributed_revenue_arr, wsCcy)}`} className="lg:col-span-2">
          <div className="p-4 h-[280px] min-h-[280px]">
            {summary.attributed_revenue_arr === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center">
                <DollarSign className="w-8 h-8 text-zinc-700 mb-3" />
                <div className="font-mono text-[11px] text-zinc-500 max-w-xs">
                  No monetization paths use this source yet.<br />
                  <Link to="/app/monetization" className="text-[#FFB000] hover:underline">Map opportunities →</Link>
                </div>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={contribution_series}>
                  <defs>
                    <linearGradient id="srcGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#FFB000" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#FFB000" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#1a1a1a" vertical={false} />
                  <XAxis dataKey="month" stroke="#71717A" tick={{ fontFamily: "JetBrains Mono", fontSize: 10 }} />
                  <YAxis stroke="#71717A" tick={{ fontFamily: "JetBrains Mono", fontSize: 10 }} tickFormatter={(v) => fmtCurrency(v, wsCcy)} />
                  <Tooltip
                    contentStyle={{ background: "#000", border: "1px solid #262626", fontFamily: "JetBrains Mono", fontSize: 11 }}
                    formatter={(v) => fmtCurrency(v, wsCcy)}
                  />
                  <Area type="monotone" dataKey="contribution" stroke="#FFB000" strokeWidth={2} fill="url(#srcGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </Panel>

        <Panel title="// PAIRS BEST WITH" testId="pairs-with" meta={<Network className="w-3.5 h-3.5" />}>
          <div className="divide-y divide-zinc-900">
            {pairs_with.length === 0 && (
              <div className="p-6 text-center font-mono text-[11px] text-zinc-600">
                No co-occurring sources yet.
              </div>
            )}
            {pairs_with.map(p => {
              const PI = ICONS[p.type] || Database;
              return (
                <div key={p.type} className="p-3 flex items-center gap-3" data-testid={`pair-${p.type}`}>
                  <div className="w-8 h-8 border border-zinc-800 flex items-center justify-center flex-shrink-0">
                    <PI className="w-4 h-4 text-zinc-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold text-xs">{p.type}</div>
                    <div className="font-mono text-[10px] text-zinc-500">co-fuels {p.shared_opps} pathway{p.shared_opps === 1 ? "" : "s"}</div>
                  </div>
                  <div className="font-mono text-[#FFB000] text-xs">×{p.shared_opps}</div>
                </div>
              );
            })}
          </div>
        </Panel>
      </div>

      {/* monetization pathways */}
      <Panel title={`// MONETIZATION PATHWAYS (${opportunities.length})`} testId="mapped-opportunities" meta={<DollarSign className="w-3.5 h-3.5 text-[#FFB000]" />}>
        <div className="divide-y divide-zinc-900">
          {opportunities.length === 0 && (
            <div className="p-8 text-center font-mono text-[11px] text-zinc-500">
              No monetization opportunities mapped to this source yet.
              <Link to="/app/monetization" className="block text-[#FFB000] hover:underline mt-2">Run Monetization Map →</Link>
            </div>
          )}
          {opportunities.slice(0, 10).map(o => (
            <div key={o.id} className="p-5" data-testid={`opp-${o.id}`}>
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="min-w-0 flex-1">
                  <h3 className="font-heading font-bold text-base mb-1">{o.title}</h3>
                  <p className="text-sm text-zinc-500 leading-relaxed">{o.description}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="font-mono text-2xl text-[#00E676] font-medium">{fmtCurrency(o.estimated_revenue, o.estimated_revenue_currency)}</div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">/ year</div>
                </div>
              </div>
              {/* flow row */}
              <div className="flex items-center gap-2 flex-wrap font-mono text-[10px]">
                <div className="flex flex-wrap gap-1">
                  {o.data_inputs.map(di => (
                    <span key={di} className={`px-2 py-1 border uppercase tracking-widest ${di === s.type ? "bg-[#FFB000]/10 border-[#FFB000]/50 text-[#FFB000]" : "border-zinc-800 text-zinc-500"}`}>
                      {di}
                    </span>
                  ))}
                </div>
                <ChevronRight className="w-3 h-3 text-zinc-700" />
                <span className="px-2 py-1 border border-zinc-700 text-zinc-300 uppercase tracking-widest">
                  {o.ai_process}
                </span>
                <ChevronRight className="w-3 h-3 text-zinc-700" />
                <span className="px-2 py-1 border border-[#00E676]/40 text-[#00E676] uppercase tracking-widest">
                  {o.revenue_output}
                </span>
                <span className="ml-auto font-mono text-[10px] text-zinc-600">
                  {o.difficulty} · {o.time_to_value_weeks}wk TTV
                </span>
              </div>
            </div>
          ))}
        </div>
      </Panel>

      {/* mapped insights */}
      <Panel title={`// INSIGHTS FROM THIS SOURCE (${insights.length})`} testId="mapped-insights" meta={<Brain className="w-3.5 h-3.5 text-[#FFB000]" />}>
        <div className="divide-y divide-zinc-900">
          {insights.length === 0 && (
            <div className="p-8 text-center font-mono text-[11px] text-zinc-500">
              No insights have referenced this source yet.
              <Link to="/app/insights" className="block text-[#FFB000] hover:underline mt-2">Generate insights →</Link>
            </div>
          )}
          {insights.slice(0, 12).map(ins => {
            const TI = TYPE_ICON[ins.type] || TrendingUp;
            return (
              <div key={ins.id} className="p-4 flex items-start gap-4" data-testid={`insight-${ins.id}`}>
                <span className={`flex items-center gap-1 font-mono text-[9px] uppercase tracking-widest px-2 py-1 border ${TYPE_COLOR[ins.type] || ""} flex-shrink-0`}>
                  <TI className="w-3 h-3" />{ins.type}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {ins.kind === "change" && (
                      <span data-testid={`insight-kind-change-${ins.id}`} className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-[#00BFFF]/40 text-[#00BFFF] bg-[#00BFFF]/5 flex items-center gap-1">
                        <GitCompare className="w-2.5 h-2.5" /> CHANGE DETECTED
                      </span>
                    )}
                    <div className="text-sm text-white leading-snug">{ins.title}</div>
                  </div>
                  <div className="font-mono text-[10px] text-zinc-500 line-clamp-2">{ins.description}</div>
                  <div className="flex gap-3 mt-2 font-mono text-[9px] text-zinc-600">
                    <span>{ins.confidence}% conf</span>
                    <span>·</span>
                    <span>{ins.impact} impact</span>
                  </div>
                </div>
                <div className="font-mono text-sm text-[#00E676] flex-shrink-0">+{fmtCurrency(ins.estimated_value, ins.estimated_value_currency)}</div>
              </div>
            );
          })}
        </div>
      </Panel>
    </div>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className="bg-black p-4">
      <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-mono text-2xl font-medium mt-1 ${accent}`}>{value}</div>
    </div>
  );
}

const STATUS_STYLE = {
  pass: { icon: CheckCircle2, cls: "text-[#00E676] border-[#00E676]/30 bg-[#00E676]/5" },
  warn: { icon: AlertOctagon, cls: "text-[#FFB000] border-[#FFB000]/30 bg-[#FFB000]/5" },
  fail: { icon: XCircle, cls: "text-[#FF3B30] border-[#FF3B30]/30 bg-[#FF3B30]/5" },
  na:   { icon: AlertTriangle, cls: "text-zinc-500 border-zinc-800" },
};

function ScorecardPanel({ sc, sourceId }) {
  const overall = sc.overall_score ?? 0;
  const band = overall >= 80 ? "#00E676" : overall >= 60 ? "#FFB000" : "#FF3B30";
  const [trend, setTrend] = useState([]);
  useEffect(() => {
    if (!sourceId) return;
    api.get(`/data-sources/${sourceId}/scorecards/trend`)
      .then(r => setTrend(r.data.items || []))
      .catch(() => setTrend([]));
  }, [sourceId, sc.overall_score]);  // reload after a rescore
  return (
    <Panel testId="source-scorecard-panel" title={`// MARKET SCORECARD · ${sc.playbook_name}`} meta={<Award className="w-3.5 h-3.5 text-[#00BFFF]" />}>
      <div className="p-6">
        {/* Header with overall + regulator */}
        <div className="flex items-center gap-6 flex-wrap mb-6">
          <div className="flex items-center gap-4">
            <div className="relative w-24 h-24 flex items-center justify-center">
              <svg className="absolute inset-0 -rotate-90" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="42" fill="none" stroke="#18181b" strokeWidth="8" />
                <circle cx="50" cy="50" r="42" fill="none" stroke={band} strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={`${(overall/100)*264} 264`} />
              </svg>
              <div className="font-mono font-bold text-2xl" style={{ color: band }} data-testid="scorecard-overall">
                {overall}
              </div>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">OVERALL SCORE</div>
              <div className="font-mono text-[11px] text-zinc-400 mt-1">{sc.applicable_rules}/{sc.total_rules} rules applied</div>
              {sc.auto_selected && (
                <div className="mt-2 font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-[#00BFFF]/40 text-[#00BFFF] bg-[#00BFFF]/5 inline-block">
                  AUTO-SELECTED FROM CONTEXT
                </div>
              )}
            </div>
          </div>
          <div className="flex-1 min-w-[240px] border-l border-zinc-900 pl-5">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">REGULATOR / SOURCES</div>
            <div className="font-mono text-xs text-zinc-300 leading-relaxed">{sc.regulator}</div>
          </div>
        </div>

        {/* Trend chart — appears once we have ≥ 2 runs */}
        {trend.length >= 2 && (
          <div className="mb-6 border border-zinc-900 bg-black/40 p-4" data-testid="scorecard-trend">
            <div className="flex items-center justify-between mb-3">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#00BFFF] flex items-center gap-2">
                <LineChart className="w-3.5 h-3.5" /> SCORE TREND · {trend.length} RUNS
              </div>
              <div className="font-mono text-[10px] text-zinc-500">
                {(() => {
                  const first = trend[0].score, last = trend[trend.length - 1].score;
                  const diff = last - first;
                  const col = diff > 0 ? "text-[#00E676]" : diff < 0 ? "text-[#FF3B30]" : "text-zinc-400";
                  return <span className={col}>{diff > 0 ? "+" : ""}{diff} pts since {new Date(trend[0].created_at).toLocaleDateString()}</span>;
                })()}
              </div>
            </div>
            <div className="h-[120px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trend.map(p => ({
                  label: new Date(p.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
                  score: p.score, pass: p.pass, warn: p.warn, fail: p.fail,
                }))}>
                  <defs>
                    <linearGradient id="scoreGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={band} stopOpacity={0.5} />
                      <stop offset="100%" stopColor={band} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#1a1a1a" vertical={false} />
                  <XAxis dataKey="label" stroke="#71717A" tick={{ fontFamily: "JetBrains Mono", fontSize: 9 }} />
                  <YAxis domain={[0, 100]} stroke="#71717A" tick={{ fontFamily: "JetBrains Mono", fontSize: 9 }} width={28} />
                  <Tooltip
                    contentStyle={{ background: "#000", border: "1px solid #262626", fontFamily: "JetBrains Mono", fontSize: 10 }}
                    formatter={(v, key) => [`${v}`, key === "score" ? "Overall" : key]}
                  />
                  <Area type="monotone" dataKey="score" stroke={band} strokeWidth={2} fill="url(#scoreGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-2 font-mono text-[10px]">
              <div className="text-[#00E676]">▸ PASS: {trend[trend.length - 1].pass}</div>
              <div className="text-[#FFB000]">▸ WARN: {trend[trend.length - 1].warn}</div>
              <div className="text-[#FF3B30]">▸ FAIL: {trend[trend.length - 1].fail}</div>
            </div>
          </div>
        )}

        {/* Top 3 actions */}
        {sc.top_actions?.length > 0 && (
          <div className="mb-6 border border-[#FF3B30]/20 bg-[#FF3B30]/5 p-4">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF3B30] mb-3 flex items-center gap-2">
              <AlertOctagon className="w-3.5 h-3.5" /> PRIORITY ACTIONS ({sc.top_actions.length})
            </div>
            <div className="space-y-3" data-testid="scorecard-top-actions">
              {sc.top_actions.map((a, i) => {
                const SI = STATUS_STYLE[a.status]?.icon || AlertOctagon;
                return (
                  <div key={a.id || i} className="flex items-start gap-3">
                    <SI className={`w-4 h-4 mt-0.5 flex-shrink-0 ${a.status === "fail" ? "text-[#FF3B30]" : "text-[#FFB000]"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-[11px] text-white font-bold">{a.title}</div>
                      <div className="font-mono text-[11px] text-zinc-400 mt-0.5 leading-relaxed">{a.recommendation}</div>
                      {a.citation && (
                        <div className="font-mono text-[9px] text-zinc-600 mt-1 uppercase tracking-widest">cite · {a.citation}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Rule-by-rule grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="scorecard-results">
          {sc.results.map(r => {
            const st = STATUS_STYLE[r.status] || STATUS_STYLE.na;
            const SI = st.icon;
            return (
              <div key={r.id} className={`border p-3 ${st.cls}`} data-testid={`rule-${r.id}`}>
                <div className="flex items-start gap-2 mb-2">
                  <SI className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-[11px] font-bold uppercase tracking-wide text-white truncate">{r.title}</div>
                    <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-600 mt-0.5">{r.category}</div>
                  </div>
                  {typeof r.score === "number" && (
                    <div className="font-mono text-[11px] flex-shrink-0 opacity-80">{r.score}</div>
                  )}
                </div>
                <div className="font-mono text-[11px] text-zinc-300">
                  {r.status === "na"
                    ? <span className="italic text-zinc-500">{r.reason || "Not applicable to this dataset"}</span>
                    : <>
                        <span className="text-zinc-400">Observed:</span> {r.observed}
                        {r.expected && (
                          <div className="text-zinc-600 mt-0.5"><span className="text-zinc-500">Expected:</span> {r.expected}</div>
                        )}
                      </>
                  }
                </div>
                {r.recommendation && r.status !== "pass" && (
                  <div className="mt-2 pt-2 border-t border-zinc-800/50 font-mono text-[10px] text-zinc-500 leading-relaxed">
                    {r.recommendation}
                  </div>
                )}
                {r.citation && (
                  <div className="mt-2 font-mono text-[9px] text-zinc-700 uppercase tracking-widest">{r.citation}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </Panel>
  );
}

function DiffPanel({ diff }) {
  const rc = diff.row_count || {};
  const added = diff.columns_added || [];
  const removed = diff.columns_removed || [];
  const changed = diff.columns_changed || [];
  const rowDelta = rc.pct;
  const hasAny = added.length || removed.length || changed.length || (rowDelta != null && rowDelta !== 0);
  if (!hasAny) {
    return (
      <Panel testId="source-diff-empty" title="// CHANGES SINCE LAST UPLOAD" meta={<GitCompare className="w-3.5 h-3.5 text-zinc-500" />}>
        <div className="p-6 font-mono text-[11px] text-zinc-500">
          No meaningful changes detected vs. the prior upload.
        </div>
      </Panel>
    );
  }
  return (
    <Panel testId="source-diff-panel" title="// CHANGES SINCE LAST UPLOAD" meta={<GitCompare className="w-3.5 h-3.5 text-[#00BFFF]" />}>
      <div className="p-5 space-y-5">
        {rowDelta != null && (
          <div className="flex items-center gap-3 flex-wrap">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">ROW COUNT</div>
            <div className="font-mono text-lg text-white">{rc.from?.toLocaleString() || 0}</div>
            <ArrowUpRight className="w-4 h-4 text-zinc-600" />
            <div className="font-mono text-lg text-white">{rc.to?.toLocaleString() || 0}</div>
            {rowDelta !== 0 && (
              <span className={`font-mono text-[11px] px-2 py-0.5 border inline-flex items-center gap-1 ${
                rowDelta > 0 ? "text-[#00E676] border-[#00E676]/30" : "text-[#FF3B30] border-[#FF3B30]/30"
              }`}>
                {rowDelta > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                {rowDelta > 0 ? "+" : ""}{rowDelta}%
              </span>
            )}
          </div>
        )}

        {added.length > 0 && (
          <div className="flex items-start gap-3 flex-wrap">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
              <Plus className="w-3 h-3 text-[#00E676]" /> ADDED
            </div>
            <div className="flex gap-1.5 flex-wrap" data-testid="diff-added">
              {added.map(c => (
                <span key={c} className="font-mono text-[11px] px-2 py-0.5 border border-[#00E676]/30 text-[#00E676]">{c}</span>
              ))}
            </div>
          </div>
        )}

        {removed.length > 0 && (
          <div className="flex items-start gap-3 flex-wrap">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
              <Minus className="w-3 h-3 text-[#FF3B30]" /> REMOVED
            </div>
            <div className="flex gap-1.5 flex-wrap" data-testid="diff-removed">
              {removed.map(c => (
                <span key={c} className="font-mono text-[11px] px-2 py-0.5 border border-[#FF3B30]/30 text-[#FF3B30] line-through">{c}</span>
              ))}
            </div>
          </div>
        )}

        {changed.length > 0 && (
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
              SHIFTED · {changed.length} COLUMN{changed.length === 1 ? "" : "S"}
            </div>
            <div className="space-y-1.5" data-testid="diff-changed">
              {changed.slice(0, 12).map(ch => (
                <div key={ch.name} className="font-mono text-[11px] flex items-start gap-3 border-l-2 border-[#00BFFF]/40 pl-3 py-1">
                  <span className="text-white min-w-[140px] truncate">{ch.name}</span>
                  <span className="text-zinc-500 flex-1">
                    {ch.null_pct && <span className="mr-3">nulls {ch.null_pct.from}% → {ch.null_pct.to}%</span>}
                    {ch.cardinality && (
                      <span className={`mr-3 ${ch.cardinality.pct > 0 ? "text-[#00E676]" : "text-[#FF3B30]"}`}>
                        card {ch.cardinality.from.toLocaleString()} → {ch.cardinality.to.toLocaleString()} ({ch.cardinality.pct > 0 ? "+" : ""}{ch.cardinality.pct}%)
                      </span>
                    )}
                    {ch.numeric && Object.entries(ch.numeric).map(([k, v]) => (
                      <span key={k} className={`mr-3 ${v.pct > 0 ? "text-[#00E676]" : "text-[#FF3B30]"}`}>
                        {k} {v.from} → {v.to} ({v.pct > 0 ? "+" : ""}{v.pct}%)
                      </span>
                    ))}
                    {ch.type && <span className="mr-3 text-[#FFB000]">type {ch.type.from} → {ch.type.to}</span>}
                  </span>
                </div>
              ))}
              {changed.length > 12 && (
                <div className="font-mono text-[10px] text-zinc-600 pl-3">…and {changed.length - 12} more</div>
              )}
            </div>
          </div>
        )}

        <div className="border-t border-zinc-900 pt-4">
          <div className="font-mono text-[10px] text-zinc-600">
            Run <span className="text-[#FFB000]">ANALYZE (DIFF-AWARE)</span> above to get DARA insights focused on these changes.
          </div>
        </div>
      </div>
    </Panel>
  );
}
